import { useLocation, useParams } from 'react-router-dom';
import type { NetworksApiResponse, PageTypeResult as PageMetadataResult } from '@/types';
import { APP_TO_DEFAULT_PAGE_MAP, PAGE_TO_APP_MAP, PAGE_TYPES } from '@/utils/constants';
import { getCurrentRouteParams } from '@/utils/routeUtils';

/**
 * Derives page type and app type from URL, with network-based fallback ONLY when
 * URL doesn't contain a page type. This respects user intent and shows "no access"
 * if they don't have access to the requested app type.
 *
 * @param allNetworks - Optional: All networks to use for app type fallback when URL is ambiguous
 * @returns {PageMetadataResult} The page type and app type.
 */
const usePageMetadata = (allNetworks?: NetworksApiResponse): PageMetadataResult => {
  const { pathname } = useLocation();
  const params = useParams();
  const { networkId } = getCurrentRouteParams(params);

  // Try to find page type from URL pathname
  const pageFromUrl = PAGE_TYPES.find((type) => pathname.includes(type));

  if (pageFromUrl) {
    return {
      app: PAGE_TO_APP_MAP[pageFromUrl],
      page: pageFromUrl,
    };
  }

  // URL parsing found no page type - use network-based fallback
  if (networkId && allNetworks) {
    const isRevenueNetwork = allNetworks.revenue?.some((n) => n.id === networkId);
    if (isRevenueNetwork) {
      return {
        app: 'revenue',
        page: APP_TO_DEFAULT_PAGE_MAP['revenue'],
      };
    }

    const isInsightsNetwork = allNetworks.insights?.some((n) => n.id === networkId);
    if (isInsightsNetwork) {
      return {
        app: 'insights',
        page: APP_TO_DEFAULT_PAGE_MAP['insights'],
      };
    }
  }

  return {
    app: 'revenue',
    page: 'earnings',
  };
};

export default usePageMetadata;
